import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule, TableLazyLoadEvent } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { SessionStorageService } from '../../../services/session-storage.service';
import { BankReconciliationService } from '../bank-reconciliation.service';
import { BankReconciliationSession, BankStatement, BankStatementLine, BankStatementLineStatus } from '../models/bank-reconciliation.model';

@Component({
    selector: 'app-bank-statement-detail',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, DatePickerModule, InputTextModule, SelectModule, TableModule, TagModule,
        PageShellComponent, PageHeroComponent, PageMetricStripComponent, PageDataPanelComponent],
    template: `
        <app-page-shell>
            <app-page-hero eyebrow="Bank Statement" [title]="title" [subtitle]="subtitle">
                <div actions>
                    <button pButton type="button" icon="pi pi-list" label="Statements" [outlined]="true" (click)="router.navigate(['/accounts/enterprise/bank-reconciliation/statements'])"></button>
                    <button pButton type="button" icon="pi pi-sliders-h" [label]="activeSession ? 'Open Session' : 'Start Reconciliation'" [outlined]="!!activeSession" [loading]="startingSession" (click)="openOrStartSession()"></button>
                    <button pButton type="button" icon="pi pi-chart-bar" label="Report" [outlined]="true" *ngIf="activeSession" (click)="router.navigate(['/accounts/enterprise/bank-reconciliation/reports/session', activeSession.sessionId])"></button>
                    <button pButton type="button" icon="pi pi-upload" label="Import Another" (click)="router.navigate(['/accounts/enterprise/bank-reconciliation/statements/import'])"></button>
                </div>
            </app-page-hero>

            <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

            <app-page-data-panel title="Statement Lines" [resultText]="totalRecords + ' rows'">
                <div actions class="toolbar">
                    <input pInputText placeholder="Reference or description" [(ngModel)]="searchText" (keyup.enter)="loadLines()">
                    <p-select [options]="statusOptions" [(ngModel)]="selectedStatus" placeholder="All statuses" [showClear]="true" (onChange)="loadLines()"></p-select>
                    <p-datePicker [(ngModel)]="dateRange" selectionMode="range" dateFormat="yy-mm-dd" placeholder="Date range" (onSelect)="loadLines()"></p-datePicker>
                    <button pButton type="button" icon="pi pi-search" label="Search" [outlined]="true" (click)="loadLines()"></button>
                </div>
                <p-table [value]="lines" [loading]="loading" [lazy]="true" [paginator]="true" [rows]="rowsPerPage" [totalRecords]="totalRecords"
                         styleClass="p-datatable-sm" (onLazyLoad)="onLazyLoad($event)">
                    <ng-template pTemplate="header">
                        <tr>
                            <th>#</th><th>Date</th><th>Value Date</th><th>Reference</th><th>Description</th><th>Counterparty</th><th>Debit</th><th>Credit</th><th>Balance</th><th>Status</th>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-line>
                        <tr>
                            <td>{{ line.lineNumber }}</td>
                            <td>{{ line.transactionDate }}</td>
                            <td>{{ line.valueDate || '-' }}</td>
                            <td>{{ line.bankReference || line.customerReference || '-' }}</td>
                            <td>{{ line.description || '-' }}</td>
                            <td>{{ line.counterparty || '-' }}</td>
                            <td>{{ line.debit | number:'1.2-2' }}</td>
                            <td>{{ line.credit | number:'1.2-2' }}</td>
                            <td>{{ line.runningBalance | number:'1.2-2' }}</td>
                            <td><p-tag [value]="line.status" [severity]="line.status === 'MATCHED' ? 'success' : line.status === 'UNMATCHED' ? 'warn' : 'info'"></p-tag></td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                        <tr><td colspan="10">No lines found for the selected filters.</td></tr>
                    </ng-template>
                </p-table>
            </app-page-data-panel>
        </app-page-shell>
    `,
    styles: [`
        .toolbar { display: flex; flex-wrap: wrap; gap: .5rem; }
        .toolbar input { min-width: 240px; }
        .toolbar p-select { min-width: 190px; }
    `]
})
export class BankStatementDetailComponent implements OnInit {
    statement?: BankStatement;
    lines: BankStatementLine[] = [];
    totalRecords = 0;
    rowsPerPage = 50;
    page = 0;
    loading = false;
    searchText = '';
    selectedStatus?: BankStatementLineStatus | '';
    dateRange?: Date[];
    metrics: PageMetric[] = [];
    activeSession?: BankReconciliationSession;
    startingSession = false;
    readonly statusOptions = ['UNMATCHED', 'SUGGESTED', 'MATCHED', 'PARTIAL', 'EXCEPTION', 'EXCLUDED'].map(value => ({ label: value, value }));

    constructor(
        private route: ActivatedRoute,
        private bankReconciliationService: BankReconciliationService,
        private sessionStorageService: SessionStorageService,
        public router: Router
    ) {}

    get title(): string {
        return this.statement?.statementNumber || (this.statement ? `Statement ${this.statement.statementId}` : 'Bank Statement');
    }

    get subtitle(): string {
        if (!this.statement) return 'Loading statement header and imported bank lines.';
        return `${this.statement.periodStartDate || '-'} to ${this.statement.periodEndDate || '-'} | ${this.statement.currencyCode} | ${this.statement.status}`;
    }

    ngOnInit(): void {
        const statementId = Number(this.route.snapshot.paramMap.get('statementId'));
        this.bankReconciliationService.getStatement(statementId).subscribe(statement => {
            this.statement = statement;
            this.loadStatementSessions();
            this.loadLines();
        });
    }

    onLazyLoad(event: TableLazyLoadEvent): void {
        this.page = Math.floor((event.first ?? 0) / (event.rows ?? this.rowsPerPage));
        this.rowsPerPage = event.rows ?? this.rowsPerPage;
        this.loadLines();
    }

    loadLines(): void {
        if (!this.statement) return;
        this.loading = true;
        this.bankReconciliationService.getStatementLines(this.statement.statementId, {
            status: this.selectedStatus,
            searchText: this.searchText,
            startDate: this.toDate(this.dateRange?.[0]),
            endDate: this.toDate(this.dateRange?.[1]),
            page: this.page,
            size: this.rowsPerPage
        }).subscribe({
            next: response => {
                this.lines = response.items ?? [];
                this.totalRecords = response.count ?? 0;
                this.metrics = [
                    { label: 'Total lines', value: response.count ?? 0, tone: 'info' },
                    { label: 'Unmatched', value: response.unmatchedCount ?? 0, tone: 'warn' },
                    { label: 'Debit total', value: this.amount(response.debitTotal), tone: 'danger' },
                    { label: 'Credit total', value: this.amount(response.creditTotal), tone: 'success' }
                ];
                this.loading = false;
            },
            error: () => this.loading = false
        });
    }

    loadStatementSessions(): void {
        if (!this.statement) return;
        this.bankReconciliationService.getStatementSessions(this.statement.statementId).subscribe(sessions => {
            this.activeSession = (sessions ?? []).find(session => session.status !== 'CLOSED' && session.status !== 'CANCELLED') ?? sessions?.[0];
        });
    }

    openOrStartSession(): void {
        if (!this.statement) return;
        if (this.activeSession) {
            this.router.navigate(['/accounts/enterprise/bank-reconciliation/sessions', this.activeSession.sessionId]);
            return;
        }
        this.startingSession = true;
        this.bankReconciliationService.createSession({
            owningCompanyId: this.statement.owningCompanyId,
            bankAccountId: this.statement.bankAccountId,
            statementId: this.statement.statementId,
            periodStartDate: this.statement.periodStartDate,
            periodEndDate: this.statement.periodEndDate,
            ownerCustomerId: this.sessionStorageService.getCustomer()?.id
        }).subscribe({
            next: session => {
                this.startingSession = false;
                this.router.navigate(['/accounts/enterprise/bank-reconciliation/sessions', session.sessionId]);
            },
            error: () => this.startingSession = false
        });
    }

    private amount(value?: number): string {
        return `${value ?? 0}`;
    }

    private toDate(value?: Date): string | undefined {
        return value ? value.toISOString().slice(0, 10) : undefined;
    }
}
